import { createSelector } from 'reselect'
import { RootState } from 'src/state/types'
import { assets } from './constants'
import {
  TMaybeId,
  TPaymentRow,
  TPreferredAsset,
  TReferenceLookup,
  TSuspenseAccount,
  TWalletBalance,
  TWalletState,
} from './types'

const state = (state: RootState): RootState => state

const walletState = (state: RootState): TWalletState => state.api.wallet

/**
 * Resolve a balance to the asset the UI shows: the dummy ZAR coin if the wallet holds it,
 * otherwise real ZAR. See {@link TPreferredAsset} for what `null` and `false` mean.
 */
const getPreferredAsset = (balance: TWalletBalance | undefined): TPreferredAsset => {
  if (!balance) {
    return null
  }
  if (balance.exists) {
    const asset = balance.assets.find(p => p.name === assets.dummy) ?? balance.assets.find(p => p.name === assets.zar)
    if (asset) {
      return asset
    }
  }
  return false
}

/** `null` → `null` (not loaded), `false` → `0` (no wallet), otherwise the asset's balance. */
const toBalance = (asset: TPreferredAsset): number | null =>
  asset === null ? null : asset === false ? 0 : asset.balance

const partyBalances = (ws: TWalletState, partyId: TMaybeId) => (partyId ? ws.balances[partyId] : undefined)

/**
 * `accountType` is typed `string`, not `TWalletAccountType`: callers pass party-account tags
 * straight through from props, and it is only ever compared against the wire value.
 */
const findAccountBalance = (
  ws: TWalletState,
  partyId: TMaybeId,
  accountId: TMaybeId,
  accountType: string,
): TWalletBalance | undefined =>
  partyBalances(ws, partyId)?.accountBalances.find(b => b.account === accountId && b.accountType === accountType)

export const getPayments = createSelector(walletState, (ws): TPaymentRow[] => {
  const { payments } = ws.paymentHistory
  const known = new Set(payments.map(p => p.payment))
  return payments.concat(ws.approvedPayments.filter(p => !known.has(p.payment)))
})

/**
 * Whether the payments screen should keep polling: a row will still change on its own while
 * it is an approval the wallet has not picked up yet, or a payment waiting in the queue.
 */
export const shouldAutoRefreshPayments = createSelector(getPayments, (payments): boolean =>
  payments.some(p => p.paymentStatus === 'Approved' || p.paymentStatus === 'Queue'),
)

export const getPaymentById = createSelector(
  getPayments,
  payments =>
    (id: string): TPaymentRow | undefined =>
      payments.find(p => p.payment === id),
)

export const walletExists = createSelector(
  walletState,
  ws =>
    (partyId: TMaybeId): boolean =>
      partyBalances(ws, partyId)?.globalBalance.exists ?? false,
)

export const getGlobalBalanceAsset = createSelector(
  walletState,
  ws =>
    (partyId: TMaybeId): TPreferredAsset =>
      getPreferredAsset(partyBalances(ws, partyId)?.globalBalance),
)

/** Unlike the account balances below, a missing global balance reads as `0`, not `null`. */
export const getGlobalBalance = createSelector(state, s => (partyId: TMaybeId): number => {
  const asset = getGlobalBalanceAsset(s)(partyId)
  return !asset ? 0 : asset.balance
})

/**
 * An account balance with no `accountId` resolves to `false` (→ `0`) rather than `null`:
 * there is nothing to wait for.
 */
export const getBalanceAsset = createSelector(
  walletState,
  ws =>
    (partyId: TMaybeId, accountId: TMaybeId, accountType: string = 'Tenant'): TPreferredAsset =>
      accountId ? getPreferredAsset(findAccountBalance(ws, partyId, accountId, accountType)) : false,
)

export const getBalance = createSelector(
  state,
  state =>
    (partyId: TMaybeId, accountId: TMaybeId, accountType: string = 'Tenant'): number | null =>
      toBalance(getBalanceAsset(state)(partyId, accountId, accountType)),
)

export const hasDepositAccount = createSelector(
  state,
  state =>
    (partyId: TMaybeId, accountId: TMaybeId): boolean =>
      !!getBalanceAsset(state)(partyId, accountId, 'Trust'),
)

export const getDepositAccountBalance = createSelector(
  state,
  state =>
    (partyId: TMaybeId, accountId: TMaybeId): number | null =>
      toBalance(getBalanceAsset(state)(partyId, accountId, 'Trust')),
)

export const getWithdrawalModalStatus = createSelector(walletState, ws => (): boolean => ws.isWithdrawalModalOpen)

export const isLoading = createSelector(
  walletState,
  ws =>
    (action: keyof TWalletState['loading']): boolean =>
      ws.loading[action],
)

/**
 * Suspense Accounts
 */
export const getUnallocatedSuspenseAccounts = createSelector(
  walletState,
  (ws): TSuspenseAccount[] => ws.unallocatedSuspenseAccounts,
)

export const getAllocatedSuspenseAccounts = createSelector(
  walletState,
  (ws): TSuspenseAccount[] => ws.allocatedSuspenseAccounts,
)

export const getReferenceByTxId = createSelector(
  walletState,
  ws =>
    (txId: TMaybeId): TSuspenseAccount | undefined =>
      [...ws.unallocatedSuspenseAccounts, ...ws.allocatedSuspenseAccounts].find(ref => ref.txId === txId),
)

export const getReferenceType = createSelector(
  state,
  state =>
    (txId: TMaybeId): 'allocated' | 'unallocated' =>
      getReferenceByTxId(state)(txId)?.allocatedAt ? 'allocated' : 'unallocated',
)

export const getConsolidatedBalanceAsset = createSelector(
  walletState,
  ws =>
    (partyId: TMaybeId): TPreferredAsset =>
      getPreferredAsset(partyBalances(ws, partyId)?.consolidatedBalance),
)

export const getConsolidatedBalance = createSelector(state, s => (partyId: TMaybeId): number => {
  const asset = getConsolidatedBalanceAsset(s)(partyId)
  return !asset ? 0 : asset.balance
})

export const getConsolidatedBalanceReference = createSelector(
  walletState,
  ws =>
    (partyId: TMaybeId): string =>
      partyBalances(ws, partyId)?.consolidatedBalance?.accountReference ?? '',
)

export const getReferenceLookups = createSelector(walletState, ws => ws.referenceLookups)

export const getReferenceLookupByReference = createSelector(
  getReferenceLookups,
  lookups =>
    (reference: TMaybeId): TReferenceLookup | undefined =>
      reference ? lookups[reference] : undefined,
)

/**
 * The wallet a looked-up reference's funds actually live in. A reference lookup always answers
 * the account (NAL) wallet, but once an owner has a consolidated wallet the NAL is permanently
 * drained and the backend moves funds through the CW, so balances and `fromAccount` must use
 * `consolidatedWalletRef` whenever the lookup carries one.
 */
type TSpendableLookup = { walletRef: string; consolidatedWalletRef?: string }
export function spendableWalletRef(lookup: TSpendableLookup): string
export function spendableWalletRef(lookup: TSpendableLookup | undefined): string | undefined
export function spendableWalletRef(lookup: TSpendableLookup | undefined): string | undefined {
  return lookup?.consolidatedWalletRef ?? lookup?.walletRef
}

/**
 * Banks — the whole `{ banks }` response, not the array; see `TSupportedBanks`.
 */
export const getBanks = createSelector(walletState, ws => ws.banks)
